import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BYPASS_AUTH } from '@brand-lolly/app/_services/interceptors/http-config.interceptor';
import { SocialListeningMentionOrigin } from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-mentions.model';
import { smiUrl } from 'components';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * The AI builder's reads.
 *
 * `resolve` reads the warehouse for charts that are already chosen. It costs
 * nothing and runs every time the report is opened — including inside the
 * headless browser that renders the PDF, which is why it is served on the
 * export-token route. `comparableEntities` is the allowlist a chart may be
 * drawn about: the brand, its competitors, the reader's other brands and
 * their industries.
 *
 * Each has a form keyed by the REPORT (`resolveFor`, `entitiesFor`,
 * `catalogueFor`): the server reads the report's home from its row, so a
 * report homed on a competitor project or an industry — which has no brand —
 * is read the same way, under the owner's session, the report's share link or
 * its export token. The brand-keyed forms stay for what reads a brand itself.
 *
 * CHOOSING a chart is not here any more. It used to be `plan`, behind an agent
 * that read a sentence and picked blocks; Ana does that herself now, from the
 * same catalogue, in `sl_report_chart`. Nothing on this page asks a model
 * anything — it draws what it is given.
 *
 * Neither call invents a figure: the backend picks blocks from a fixed
 * catalogue and fills them from the same aggregations the Analytics tab is
 * drawn from.
 */

/**
 * One thing a block measures.
 *
 * The label travels with the id rather than being looked up at draw time: it is
 * what names the series in the legend, and a report exported six months from
 * now should say what it said when it was made, not what the competitor has
 * since been renamed to.
 */
export type ReportEntityKind = 'brand' | 'competitor' | 'industry';

export interface ReportBlockEntity {
	kind: ReportEntityKind;
	id: string;
	label: string;
}

/** A block as chosen — what the report stores in its settings. */
export interface ReportBlockSpec {
	block_id: string;
	/**
	 * Omitted means the catalogue's default for that block — the server fills
	 * it, and a spec authored here has no way to know what it is. Every spec
	 * that comes BACK from the server carries one.
	 */
	render?: ReportBlockRender | null;
	/** Omitted means the block's own name, filled server-side like `render`. */
	title?: string | null;
	/** The reader typed `title` themselves, in the editor. */
	title_edited_by_user?: boolean | null;
	limit?: number | null;
	/**
	 * Who the block is about. Absent means the report's own brand, which is
	 * every block written before comparison existed — and is why this is
	 * optional rather than always sent.
	 */
	entities?: ReportBlockEntity[];
	/**
	 * The chart's own read, on top of the report's (`blocks/overrides.py`):
	 * its own period and filters, its days grouped, its list in another order,
	 * the period before beside it. Absent is the report's own read, which is
	 * every chart written before its data could be changed.
	 */
	filters?: ReportBlockFilters | null;
	group_by?: ReportBlockGroupBy | null;
	sort?: ReportBlockSort | null;
	compare?: ReportBlockCompare | null;
	/**
	 * The mention a quote card quotes, by the id the Mentions list serves it
	 * under. Only a quote card carries it, and the server refuses it on a
	 * chart and on a list of mentions, which reads its own.
	 */
	mention_id?: string | null;
	/**
	 * The quote card — or every card of a list of mentions — is shown without
	 * its author. Only those two take it.
	 */
	hide_author?: boolean | null;
	// CONTRACT: the backend's BlockSpec.after, drawn by report-placement.ts —
	// absent is the end, where every chart went before it could be placed.
	after?: string;
	// CONTRACT: the backend's BlockSpec.id, at most 60 characters and unique in
	// the report. Minted by whatever puts the chart in — the section library,
	// Ana's applyReportChart, Duplicate, and the server for Add to report — and
	// kept for the chart's life, because a comment on the chart is anchored to
	// it. Absent on a chart saved before charts had one (`withChartIds`).
	id?: string | null;
}

/**
 * A chart's own period and filters — the backend's `BlockFilters`. The period
 * ends where the report's window ends, and each filter only narrows what the
 * chart counts.
 */
export interface ReportBlockFilters {
	/** A report period key; absent is the report's own period. */
	period?: string | null;
	platforms?: string[] | null;
	sentiments?: ReportBlockSentiment[] | null;
	/** Base ISO 639 codes, as every other language filter sends them. */
	languages?: string[] | null;
}

export type ReportBlockSentiment = 'positive' | 'neutral' | 'negative';

/** How a trend's days are laid out; `day` is how it has always been drawn. */
export type ReportBlockGroupBy = 'day' | 'week' | 'month';

/** The order a list is drawn in. Its top N is always the largest N. */
export type ReportBlockSort =
	| 'largest_first'
	| 'smallest_first'
	| 'alphabetical';

export type ReportBlockCompare = 'previous_period';

/**
 * What of a chart's read the editor may change, block by block — the
 * catalogue's `options`, decided on the server by what each read can answer.
 */
export type ReportBlockDataOption =
	| 'period'
	| 'platforms'
	| 'sentiments'
	| 'languages'
	| 'group_by'
	| 'limit'
	| 'sort'
	| 'compare';

/**
 * What a chart was read over, when it asked for more than the report's
 * window — the backend's `BlockScope`. It comes back WITH the numbers, from
 * the read itself, so the source line names the window its numbers came
 * from, including a share link's cut of it, and never just what was asked.
 */
export interface ReportBlockScope {
	from_date: string | null;
	to_date: string | null;
	/** The chart's own period key; null when it is the report's. */
	period: string | null;
	platforms: string[];
	sentiments: string[];
	languages: string[];
	group_by: ReportBlockGroupBy | null;
	/** The window the period before was read over, when it was. */
	previous_from_date: string | null;
	previous_to_date: string | null;
}

/**
 * Something this report's charts may be drawn about.
 *
 * The server's allowlist, not a menu assembled here: `resolve` checks specs
 * against the same list, so anything outside it is refused however the request
 * is phrased — and offering it here would be offering something that would
 * then come back empty.
 */
export interface ReportComparableEntity {
	kind: ReportEntityKind;
	id: string;
	label: string;
	/** The report's own brand — the one every chart already shows. */
	is_self: boolean;
	/**
	 * For a competitor, the competitor project it is tracked in: the library
	 * starts a chart in a report about that project from its own competitors.
	 * Null for a brand and an industry.
	 */
	project_id?: string | null;
}

export type ReportBlockRender =
	| 'line'
	| 'area'
	| 'bar'
	| 'stacked_bar'
	/** Bars on their side, sorted largest first — a leaderboard. */
	| 'ranked_bar'
	| 'pie'
	| 'doughnut'
	| 'polarArea'
	| 'matrix'
	| 'wordCloud'
	| 'table'
	| 'kpi'
	/**
	 * A card per account, the only drawing a Creator shortlist has. Kept out
	 * of `REPORT_BLOCK_RENDERS` like `quote`: no chart is switched to it, and
	 * Ana's chart activity cannot draw one.
	 */
	| 'creators'
	/** One mention's own words on a card: the only drawing a quote has. */
	| 'quote'
	/** Alerts down a line of time, newest first — `ReportAlertsTimelineComponent`. */
	| 'timeline'
	/**
	 * Each review platform's average and count over a bar per star —
	 * `ReportReviewRatingsComponent`.
	 */
	| 'rating_bars';

/**
 * What a block's read is keyed by — the backend's `BlockKey`. `brand` is a
 * record kept under the brand itself (its alerts, its reviews): drawn for the
 * report's own brand whatever it tracks, and changeable only by its period.
 */
export type ReportBlockKey = 'keywords' | 'brand';

/**
 * The same list as a value, so a caller handed a render can check one.
 *
 * Every render but `quote`, which no chart can be switched to and nobody
 * picks: a quote card is drawn as one from the moment a mention is added. The
 * list is also what Ana's `applyReportChart` offers, and a render she cannot
 * draw a chart as has no place in it — which leaves out `timeline` and
 * `rating_bars` too: only sections of the editor's library take them.
 */
export const REPORT_BLOCK_RENDERS: ReportBlockRender[] = [
	'line',
	'area',
	'bar',
	'stacked_bar',
	'ranked_bar',
	'pie',
	'doughnut',
	'polarArea',
	'matrix',
	'wordCloud',
	'table',
	'kpi'
];

/**
 * The handle a chart is pointed at by — Ana's, and the page's own.
 *
 * Not the chart's stored `id`, which is what a comment is anchored to: this is
 * the handle Ana's artifacts and her tool's answers already speak, so it stays
 * DERIVED — the block's catalogue id, plus an ordinal when the same block is
 * drawn twice.
 *
 * TRADE-OFF, and it is a real one. A derived id survives reordering and the
 * removal of any OTHER chart, which a bare index does not — drop the first of
 * five and a reference to the fourth silently lands on the fifth. What it does
 * not survive is two charts of the SAME block: their ordinals renumber when the
 * earlier one goes. So the lookup below fails closed — an id matching nothing
 * comes back as -1 and the caller says so rather than guessing — and the page
 * republishes its artifacts on every change, so the next turn reads the ids as
 * they now stand.
 */
export function reportChartIds(specs: ReportBlockSpec[]): string[] {
	const seen = new Map<string, number>();
	return specs.map(spec => {
		const nth = (seen.get(spec.block_id) ?? 0) + 1;
		seen.set(spec.block_id, nth);
		return nth === 1 ? spec.block_id : `${spec.block_id}#${nth}`;
	});
}

/** An id for a chart no report holds yet, well inside the server's 60. */
export function newChartId(): string {
	return `chart_${Date.now().toString(36)}_${Math.random()
		.toString(36)
		.slice(2, 8)}`;
}

/**
 * The same charts with an id each, or null when every one already has one — a
 * report saved before charts had ids. The editor mints them when it opens such
 * a report and saves them at once, so a comment is only ever anchored to an id
 * the server holds.
 */
export function withChartIds(
	specs: ReportBlockSpec[]
): ReportBlockSpec[] | null {
	if (specs.every(spec => !!spec.id)) {
		return null;
	}
	return specs.map(spec => (spec.id ? spec : { ...spec, id: newChartId() }));
}

/** Where the chart with this id sits, or -1 when none does. */
export function findReportChartIndex(
	specs: ReportBlockSpec[],
	chartId: string
): number {
	return reportChartIds(specs).indexOf(chartId.trim());
}

/**
 * How many charts a report holds at most.
 *
 * The server's bound on both the saved settings and a resolve request, so a
 * 21st chart would not fail on its own: the resolve would be refused whole and
 * every chart on the page would go down with it.
 */
export const MAX_REPORT_BLOCKS = 20;

/**
 * One chart a report can hold, as the server's catalogue describes it.
 *
 * Read from the server rather than kept here: the catalogue is the list the
 * resolver dispatches on, and a copy on this side would offer a chart that
 * cannot be drawn the day the two drifted.
 */
export interface ReportBlockCatalogueEntry {
	id: string;
	name: string;
	/** The question it answers, in the words a reader would ask it. */
	answers: string;
	shape: ReportBlockShape;
	/** Best first: the first is what a newly picked chart is drawn as. */
	renders: ReportBlockRender[];
	comparable: boolean;
	/**
	 * The kinds of entity it sets the report's own brand against, when it does
	 * — Content gaps, an industry's hashtags and topics beside the brand's. A
	 * chart of it may be about one entity of these kinds and nothing else.
	 */
	against_brand?: ReportEntityKind[];
	/** What of its read a chart of this block may change. */
	options?: ReportBlockDataOption[];
	/** How many rows its top N keeps when the chart names none. */
	default_limit?: number | null;
	/**
	 * The most its top N may keep, when that is fewer than any chart's: the
	 * server refuses more, so the Data control offers no more.
	 */
	max_limit?: number | null;
	/**
	 * The blocks that draw the same axis with one measure each, and the
	 * measure this one draws: a chart's measure is switched by switching its
	 * block to another of its group.
	 */
	measure_group?: string | null;
	measure?: string | null;
	/**
	 * A section only this library offers — the backend's `LIBRARY_BLOCKS`.
	 * Ana's chart tool cannot draw or change one; see `ReportBlockData`.
	 */
	library_only?: boolean;
	/**
	 * `brand` for a section kept under the brand itself: the Data control
	 * offers it no other entity to be drawn for. Absent is `keywords`.
	 */
	keyed_by?: ReportBlockKey;
}

/**
 * How a block's numbers are laid out — the backend's `BlockShape`. The shape,
 * not the block, decides what it can be redrawn as: a heatmap needs a grid, a
 * pie needs parts of one whole.
 */
export type ReportBlockShape =
	| 'series'
	| 'categories'
	| 'matrix'
	| 'rows'
	/** The accounts with the most mentions, a card each — a Creator shortlist. */
	| 'creators'
	| 'kpi'
	/** One mention, as its author wrote it — a quote card. */
	| 'mention'
	/**
	 * The period's own mentions, ranked by the read — one quote card each,
	 * read again over whatever window the report is opened for.
	 */
	| 'mentions'
	/** Things that happened at a time of their own, newest first: alert fires. */
	| 'events'
	/** Each review platform's average and count, and a spread over 1-5 stars. */
	| 'ratings';

export interface ReportBlockSeries {
	key: string;
	label: string;
	/**
	 * In `labels` order. The backend never sends a null; a fixed section can,
	 * for a value that could not be read — a site whose visits are unknown —
	 * which is drawn as a gap and never as a zero nobody measured.
	 */
	values: Array<number | null>;
	/**
	 * Set only by the fixed sections, which carry their own palette: a
	 * sentiment line redrawn as a pie keeps the green it had, rather than the
	 * one the added charts use.
	 */
	color?: string;
	/**
	 * `%` for a share, so a card or a table says what the number is — a fixed
	 * section's voice share, and the backend's own shares (share of voice, a
	 * sentiment split as percentages), which send null for a count.
	 */
	unit?: '%';
}

/**
 * One day a trend marks, and what happened on it — the backend's
 * `BlockAnnotation`, written from that day's own counts and never by a model.
 */
export interface ReportBlockAnnotation {
	/** The day it marks, as the trend's `labels` name it. */
	date: string;
	/** How big the day was beside the window's ordinary day. */
	label: string;
	/** What carried it: a post, an account, a platform or a keyword. */
	text: string;
}

export interface ReportBlockColumn {
	key: string;
	label: string;
	kind: 'text' | 'number';
}

/** How many of a block's rated reviews carry this many stars. */
export interface ReportBlockStarCount {
	stars: number;
	count: number;
}

/**
 * One mention, as a quote card draws it — alone, or as one card of a list of
 * mentions — the backend's `BlockQuote`, every field read from the post.
 *
 * With the author hidden, the author's fields and the link arrive as null:
 * the server leaves them out of the answer, so neither the shared page nor
 * the PDF ever holds a name the card does not show.
 */
export interface ReportBlockQuote {
	mention_id: string;
	/** As the feed names it: `x`, `reddit`, `instagram`, `news`… */
	platform: string;
	text: string;
	/** A web page's headline; empty for a social post. */
	title: string;
	/** When it was posted, as an ISO instant. */
	posted_at: string | null;
	url: string | null;
	author_name: string | null;
	author_handle: string | null;
	author_image: string | null;
	author_verified: boolean;
	author_hidden: boolean;
	likes: number;
	comments: number;
	/** Null is not measured, as on the mention card — never a zero. */
	views: number | null;
	engagement_rate: number | null;
	reach: number | null;
	sentiment: 'positive' | 'neutral' | 'negative';
	// CONTRACT: quote_of (reports/blocks/quote.py) copies fields one by one, so both arrive only once it copies them from the MentionItem; absent reads as earned.
	origin?: SocialListeningMentionOrigin;
	/** The post a comment answers; empty unless `origin` is `comment_on_own_post`. */
	parent_url?: string | null;
}

/**
 * One account on a Creator shortlist, as its card draws it — the backend's
 * `BlockCreator`. Its counts are the window's, read the way Top voices reads
 * them; who it is comes from its posts and from Discovery's profile.
 */
export interface ReportBlockCreator {
	/** As the feed names it: `instagram`, `tiktok`, `youtube`, `x`, `reddit`… */
	platform: string;
	name: string;
	/** Without an @. */
	handle: string;
	avatar_url: string | null;
	mentions: number;
	positive: number;
	neutral: number;
	negative: number;
	/**
	 * CONTRACT: PROFILE DATA, never a figure for the window — the followers
	 * the account's profile shows, and every surface labels them so. Null is
	 * not known, never zero.
	 */
	profile_followers: number | null;
	/**
	 * The creator's Discovery profile, which the editor opens. Null when
	 * Discovery has none; only Instagram, TikTok and YouTube ever have one.
	 */
	discovery_id: number | null;
}

/** A block as answered. Every number in it was counted, none was written. */
export interface ReportBlockData {
	block_id: string;
	render: ReportBlockRender;
	title: string;
	/**
	 * How the numbers are laid out, and every render the block may be
	 * switched to — the catalogue's list, so the type menu offers exactly what
	 * a save accepts. Absent on a block that could not be read.
	 */
	shape?: ReportBlockShape | null;
	renders?: ReportBlockRender[];
	labels: string[];
	series: ReportBlockSeries[];
	/**
	 * The days a trend marks — Spikes and why — in date order, each one of
	 * `labels`. Drawn as numbered marks on the chart and listed under it.
	 */
	annotations?: ReportBlockAnnotation[];
	columns: ReportBlockColumn[];
	rows: Array<Record<string, string | number>>;
	/**
	 * For a block of ratings: its rated reviews over one to five stars, five
	 * first. Empty when none of them carries a rating.
	 */
	distribution?: ReportBlockStarCount[];
	figures: Record<string, string>;
	/** A Creator shortlist's accounts, most mentions first. */
	creators?: ReportBlockCreator[];
	/** A quote card's mention. Null on every chart, and on a quote whose
	 *  mention is gone, which comes back `empty` with the reason instead. */
	quote?: ReportBlockQuote | null;
	/** A list of mentions' cards, in the order the list ranks them. An
	 *  empty list comes back `empty`, with a note saying what it did not find. */
	quotes?: ReportBlockQuote[];
	total: number | null;
	/** Nothing to draw. Render the note, not an empty chart. */
	empty: boolean;
	note: string | null;
	/** What it was read over, when that is more than the report's window. */
	scope?: ReportBlockScope | null;
	/**
	 * CONTRACT: the backend's `BlockData.library_only`, set on every answer —
	 * drawn, empty or unreadable — for a section only the editor's library
	 * offers. Ana's `sl_report_chart` has no such block, so the page publishes
	 * no chart artifact for it.
	 */
	library_only?: boolean;
	/**
	 * Presentation hints only the fixed sections set: a colour per label, for
	 * the drawings where a bar or a slice IS a label and the section's table
	 * beside it already names that colour; what the labels are, for the first
	 * column of a table ("Country", "Hashtag"); and whether the section already
	 * prints a legend of its series above the chart, which a redrawn chart
	 * must not print a second time.
	 */
	labelColors?: string[];
	labelColumn?: string;
	seriesLegendShown?: boolean;
}

export interface ReportBlockResolveRequest {
	blocks: ReportBlockSpec[];
	from_date?: string | null;
	to_date?: string | null;
	platforms?: string[] | null;
	language?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ReportBlocksService {
	constructor(private http: HttpClient) {}

	private url(
		action: 'resolve' | 'entities' | 'catalogue',
		brandId?: string
	): string {
		const builder = smiUrl()
			.add('social-listening')
			.add('reports')
			.add('blocks')
			.add(action);
		return brandId
			? builder.query('brand_id', brandId).done()
			: builder.done();
	}

	/**
	 * Who this report's charts may be compared against.
	 *
	 * The brand itself, the competitors set up against it, and the reader's
	 * other brands. Read here so a chart can be checked against the allowlist
	 * BEFORE it is sent: an entity the server would refuse comes back as an
	 * empty block with a note, which is a hole in the middle of a document
	 * rather than a message somebody can act on.
	 */
	comparableEntities(brandId: string): Observable<ReportComparableEntity[]> {
		return this.http
			.get<ReportComparableEntity[]>(this.url('entities', brandId))
			.pipe(map(entities => entities ?? []));
	}

	/** Every chart a report can hold, for the editor's section library. */
	catalogue(brandId: string): Observable<ReportBlockCatalogueEntry[]> {
		return this.http
			.get<ReportBlockCatalogueEntry[]>(this.url('catalogue', brandId))
			.pipe(map(entries => entries ?? []));
	}

	/** {@link comparableEntities}, for the report's own home. */
	entitiesFor(reportId: string): Observable<ReportComparableEntity[]> {
		return this.http
			.get<ReportComparableEntity[]>(this.reportUrl(reportId, 'entities'))
			.pipe(map(entities => entities ?? []));
	}

	/**
	 * {@link catalogue}, for the report's own home: without the sections kept
	 * under a brand when the report has none to keep them under.
	 */
	catalogueFor(reportId: string): Observable<ReportBlockCatalogueEntry[]> {
		return this.http
			.get<ReportBlockCatalogueEntry[]>(
				this.reportUrl(reportId, 'catalogue')
			)
			.pipe(map(entries => entries ?? []));
	}

	resolve(
		brandId: string,
		blocks: ReportBlockSpec[],
		range?: { start: string; end: string },
		options: { bypassAuth?: boolean; language?: string | null } = {}
	): Observable<ReportBlockData[]> {
		return this.post(this.url('resolve', brandId), blocks, range, options);
	}

	/**
	 * {@link resolve}, for the report's own home. Under a share link the server
	 * draws only the charts the report has saved, over the link's window.
	 */
	resolveFor(
		reportId: string,
		blocks: ReportBlockSpec[],
		range?: { start: string; end: string },
		options: { bypassAuth?: boolean; language?: string | null } = {}
	): Observable<ReportBlockData[]> {
		return this.post(
			this.reportUrl(reportId, 'resolve'),
			blocks,
			range,
			options
		);
	}

	private reportUrl(
		reportId: string,
		action: 'resolve' | 'entities' | 'catalogue'
	): string {
		return smiUrl()
			.add('social-listening')
			.add('reports')
			.add(reportId)
			.add('blocks')
			.add(action)
			.done();
	}

	private post(
		url: string,
		blocks: ReportBlockSpec[],
		range: { start: string; end: string } | undefined,
		options: { bypassAuth?: boolean; language?: string | null }
	): Observable<ReportBlockData[]> {
		if (!blocks.length) {
			return new Observable<ReportBlockData[]>(subscriber => {
				subscriber.next([]);
				subscriber.complete();
			});
		}

		const body: ReportBlockResolveRequest = {
			blocks,
			from_date: range?.start ?? null,
			to_date: range?.end ?? null,
			language: options.language ?? null
		};

		// In the export there is no session to attach: Gotenberg puts the
		// short-lived export token on every request the headless browser makes,
		// and the interceptor must be told to leave that alone. Same reason the
		// settings read takes the flag.
		return this.http
			.post<ReportBlockData[]>(url, body, {
				context: options.bypassAuth
					? new HttpContext().set(BYPASS_AUTH, true)
					: undefined
			})
			.pipe(map(blocksData => blocksData ?? []));
	}
}
